import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Text,
  Card,
  Badge,
  SearchInput,
  FilterChips,
  StatusDot,
  EmptyState,
  Modal,
  Input,
} from '@teals/ui-web';
import {
  useGetAgentsQuery,
  useCreateAgentMutation,
  type Agent,
} from '../../store/api';

type AgentScope = Agent['scope'];

const SCOPE_BADGES: Record<
  AgentScope,
  { variant: 'primary' | 'info' | 'warning'; label: string }
> = {
  global: { variant: 'primary', label: 'Global' },
  organisation: { variant: 'info', label: 'Organisation' },
  application: { variant: 'warning', label: 'Application' },
};

const SCOPE_FILTER_CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'global', label: 'Global' },
  { id: 'organisation', label: 'Organisation' },
  { id: 'application', label: 'Application' },
];

const VERSION_FILTER_CHIPS = [
  { id: 'all', label: 'All versions' },
  { id: 'draft', label: 'Draft' },
  { id: 'sandbox', label: 'Sandbox' },
  { id: 'published', label: 'Published' },
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;

  @media (max-width: 767px) {
    padding: 1.5rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 200px;
  max-width: 320px;
`;

const AgentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  align-items: stretch;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const AgentCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
  min-width: 0;
  cursor: pointer;
  border: 1px solid var(--border);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
`;

const CardTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(1.5em * 2);
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border);
`;

const MetaText = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const LoadingState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted);
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const SortSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--bg-surface);
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const Agents: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [versionFilter, setVersionFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'updated'>('name');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const { data: agents = [], isLoading, isError } = useGetAgentsQuery();
  const [createAgent, { isLoading: isCreating }] = useCreateAgentMutation();

  const filteredAgents = useMemo(() => {
    const filtered = agents.filter((agent) => {
      const q = search.toLowerCase();
      const matchesSearch =
        !search ||
        agent.name.toLowerCase().includes(q) ||
        agent.description.toLowerCase().includes(q) ||
        agent.agentName.toLowerCase().includes(q);
      const matchesScope = scopeFilter === 'all' || agent.scope === scopeFilter;
      const matchesVersion =
        versionFilter === 'all' || agent.versionStatus === versionFilter;
      return matchesSearch && matchesScope && matchesVersion;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === 'updated') {
        return (b.updatedAt || '').localeCompare(a.updatedAt || '');
      }
      return a.name.localeCompare(b.name);
    });
  }, [agents, search, scopeFilter, versionFilter, sortBy]);

  const scopeChips = useMemo(
    () =>
      SCOPE_FILTER_CHIPS.map((chip) => ({
        ...chip,
        count:
          chip.id === 'all'
            ? agents.length
            : agents.filter((a) => a.scope === chip.id).length,
      })),
    [agents],
  );

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const created = await createAgent({
        name: newName.trim(),
        description: newDesc.trim() || undefined,
      }).unwrap();
      setCreateOpen(false);
      setNewName('');
      setNewDesc('');
      navigate(`/agents/${encodeURIComponent(created.id)}?tab=configure`);
    } catch {
      // keep modal open
    }
  };

  return (
    <Page>
      <Header>
        <HeaderCopy>
          <Text variant="h3">Agents</Text>
          <Subtitle>Build, test, and publish agents</Subtitle>
        </HeaderCopy>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          Create Agent
        </Button>
      </Header>

      <Toolbar>
        <ToolbarRow>
          <SearchWrap>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search agents..."
            />
          </SearchWrap>
          <SortSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'updated')}
            aria-label="Sort agents"
          >
            <option value="name">Sort: Name</option>
            <option value="updated">Sort: Last updated</option>
          </SortSelect>
        </ToolbarRow>
        <FilterChips
          items={scopeChips}
          activeId={scopeFilter}
          onChange={setScopeFilter}
        />
        <FilterChips
          items={VERSION_FILTER_CHIPS}
          activeId={versionFilter}
          onChange={setVersionFilter}
        />
      </Toolbar>

      {isLoading ? (
        <LoadingState>Loading agents…</LoadingState>
      ) : isError ? (
        <EmptyState
          title="Failed to load agents"
          description="Could not reach the portal agents API. Check your organisation context and try again."
        />
      ) : filteredAgents.length === 0 ? (
        <EmptyState
          title="No agents found"
          description={
            search || scopeFilter !== 'all' || versionFilter !== 'all'
              ? 'No agents match your search or filters.'
              : 'Create your first organisation agent to get started.'
          }
          action={
            search || scopeFilter !== 'all' || versionFilter !== 'all' ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setScopeFilter('all');
                  setVersionFilter('all');
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                Create Agent
              </Button>
            )
          }
        />
      ) : (
        <AgentGrid>
          {filteredAgents.map((agent) => (
            <AgentCard
              key={agent.id}
              padding={20}
              elevation="sm"
              onClick={() =>
                navigate(`/agents/${encodeURIComponent(agent.id)}`)
              }
            >
              <CardHeader>
                <CardTitle title={agent.name}>{agent.name}</CardTitle>
                <Badge variant={SCOPE_BADGES[agent.scope].variant} size="sm">
                  {SCOPE_BADGES[agent.scope].label}
                </Badge>
              </CardHeader>
              <CardDescription>
                {agent.description ||
                  [agent.llmProvider, agent.llmModel].filter(Boolean).join(' · ') ||
                  'No description'}
              </CardDescription>
              <CardFooter>
                <StatusDot
                  status={agent.versionStatus}
                  label={agent.versionStatus}
                />
                <MetaText>
                  {typeof agent.applicationCount === 'number'
                    ? `${agent.applicationCount} apps`
                    : agent.version}
                </MetaText>
              </CardFooter>
            </AgentCard>
          ))}
        </AgentGrid>
      )}

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Agent"
      >
        <ModalForm>
          <Input
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Customer Support"
          />
          <Input
            label="Description"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Optional description"
          />
          <ModalActions>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!newName.trim() || isCreating}
            >
              {isCreating ? 'Creating…' : 'Create Draft'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>
    </Page>
  );
};

export default Agents;
